import React from 'react';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';

interface DateTimeCellProps {
  /** ISO timestamp; renders an em dash when missing. */
  value: string | null | undefined;
  /** Marks recent activity: green time with a dot. */
  highlight?: boolean;
  /** Appends a compact age ("7m ago", "2d ago") after the time. */
  showRelative?: boolean;
}

/**
 * Compact age of a timestamp ("just now", "7m ago", "3h ago", "2d ago",
 * "5mo ago", "1y ago"). Short enough to share a line with the exact time in
 * a narrow table column; the full wording of dayjs' fromNow() would not fit.
 */
export function formatCompactAge(date: Dayjs, t: (key: string, options?: Record<string, unknown>) => string): string {
  const now = dayjs();
  const minutes = now.diff(date, 'minute');
  if (minutes < 1) return t('relativeTime.justNow');
  if (minutes < 60) return t('relativeTime.minutes', { count: minutes });
  const hours = now.diff(date, 'hour');
  if (hours < 24) return t('relativeTime.hours', { count: hours });
  const days = now.diff(date, 'day');
  if (days < 30) return t('relativeTime.days', { count: days });
  const months = now.diff(date, 'month');
  if (months < 12) return t('relativeTime.months', { count: months });
  return t('relativeTime.years', { count: now.diff(date, 'year') });
}

/**
 * Two-line table cell: the date on top and the exact time below (optionally
 * with a compact age), so the full timestamp is visible without a tooltip
 * and the column stays narrow.
 */
export const DateTimeCell: React.FC<DateTimeCellProps> = ({ value, highlight = false, showRelative = false }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');
  if (!value) return <span style={{ color: token.colorTextTertiary }}>—</span>;

  const date = dayjs(value);
  return (
    <div style={{ whiteSpace: 'nowrap' }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: token.colorText }}>{date.format('MMM DD, YYYY')}</div>
      <div style={{ fontSize: showRelative ? 11 : 12, color: highlight ? token.colorSuccess : token.colorTextSecondary }}>
        {highlight && <span style={{ marginRight: 4 }}>●</span>}
        {date.format('HH:mm')}
        {showRelative && <span style={{ color: token.colorTextTertiary }}> · {formatCompactAge(date, t)}</span>}
      </div>
    </div>
  );
};
